/**
 * @file rates.api.js
 * @module RatesAPI
 * @description API wrapper for managing rates: CRUD, bulk actions and histories.
 * Methods return Axios promises created by axiosInstance(token).
 */

import { axiosInstance } from "./axios"

export const RatesAPI = {
    /**
     * Get rates with pagination and optional query.
     * @param {number} limit - Number of rates to return.
     * @param {number} page - Page number for pagination.
     * @param {string} query - Optional query string for filtering rates.
     * @param {string} token - Authentication token.
     * @returns {Promise} Axios promise with the rates data.
     */
    getRates: (limit, page, query, token = "") => {
        return axiosInstance(token).get(`/rates?limit=${limit}&page=${page}${query ? `&${query}` : ""}`);
    },
    /**
     * Create new rates.
     * @param {Object} body - Rate data to be created.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise with the created rate data.
     */
    createRates: (body, token = "") => {
        return axiosInstance(token).post(`/rates`, body);
    },
    /**
     * Get a rate by its ID.
     * @param {string} rateId - ID of the rate to be retrieved.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise with the rate data.
     */
    getRateById: (rateId, token = "") => {
        return axiosInstance(token).get(`/rates/${rateId}`);
    },
    /**
     * Update a rate by its ID.
     * @param {string} rateId - ID of the rate to be updated.
     * @param {Object} body - Updated rate data.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise with the updated rate data.
     */
    updateRateById: (rateId, body, token = "") => {
        return axiosInstance(token).put(`/rates/${rateId}`, body);
    },
    /**
     * Delete a rate by its ID.
     * @param {string} rateId - ID of the rate to be deleted.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise indicating the deletion result.
     */
    deleteRateById: (rateId, token = "") => {
        return axiosInstance(token).delete(`/rates/${rateId}`);
    },
    /**
     * Get rates for a specific branch with pagination and optional query.
     * @param {string} branchId - ID of the branch.
     * @param {number} limit - Number of rates to return.
     * @param {number} page - Page number for pagination.
     * @param {string} query - Optional query string for filtering rates.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise with the branch rates data.
     */
    getRatesByBranchId: (branchId, limit, page, query, token = "") => {
        return axiosInstance(token).get(`/branches/${branchId}/rates?limit=${limit}&page=${page}${query ? `&${query}` : ""}`);
    },
    /**
     * Create a new quotation.
     * @param {Object} body - Quotation data to be created.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise with the created quotation data.
     */
    createQuotation: (body, token = "") => {
        return axiosInstance(token).post(`/create-quotation`, body);
    },
    /**
     * Delete multiple rates by their IDs.
     * @param {Object} body - Object containing an array of rate IDs to be deleted.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise indicating the deletion result.
     */
    deleteManyRatesById: (body, token = "") => {
        return axiosInstance(token).post(`/bulk-delete-rates`, body);
    },

    /**
     * Get histories for a specific rate with pagination.
     * @param {string} rateId - ID of the rate.
     * @param {number} limit - Number of histories to return.
     * @param {number} page - Page number for pagination.
     * @param {string} token - Authentication token.
     * @returns {Promise} Axios promise with the rate histories data.
     */
    getHistoriesByRateId: (rateId, limit, page, token) => {
        return axiosInstance(token).get(`/modules/rates/${rateId}/histories?limit=${limit}&page=${page}`)
    },
    /**
     * Update a history record by its ID.
     * @param {string} historyId - ID of the history record to be updated.
     * @param {Object} history - Updated history data.
     * @param {string} token - Authentication token.
     * @returns {Promise} Axios promise with the updated history data.
     */
    updateHistoryById: (historyId, history, token = "") => {
        return axiosInstance(token).put(`/histories/${historyId}`, history)
    },
    /**
     * Update any attribute of a rate by its ID.
     * @param {string} rateId - ID of the rate to be updated.
     * @param {Object} body - Object containing the attributes to be updated.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise with the updated rate data.
     */
    updateAnyAttributeById: (rateId, body, token = "") => {
        return axiosInstance(token).patch(`/rates/${rateId}`, body);
    },
    /**
     * Bulk write surcharges.
     * @param {Array} surcharges - Array of surcharge objects to be written.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise indicating the bulk write result.
     */
    surchargeBulkWrite: (surcharges = [], token = "") => {
        return axiosInstance(token).post(`/surcharges-bulk-write`, { surcharges: surcharges });
    }
}